import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import EngagementTimeline from './EngagementTimeline';
import CallDiagnostics from './CallDiagnostics';
import VoiceAudioClips from './VoiceAudioClips';
import CardsHandView from './CardsHandView';

const VERSION = 'v1.3.0';

const TABS = [
  { id: 'dealer',      label: 'Dealer Hand' },
  { id: 'timeline',    label: 'Engagement Timeline' },
  { id: 'diagnostics', label: 'Call Diagnostics' },
  { id: 'summary',     label: 'Summary' },
  { id: 'ai-context',  label: 'AI Context' },
];

const styles = `
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #888;
    font-size: 0.85rem;
    text-decoration: none;
    margin-bottom: 20px;
    transition: color 0.15s;
  }
  .back-link:hover { color: #E8845A; }
  .internal-banner {
    background: #FFF8E1;
    border: 1px solid #FFE082;
    border-radius: 10px;
    padding: 12px 18px;
    font-size: 0.85rem;
    color: #795548;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
  }
  .conv-meta {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 20px 24px;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }
  .meta-item { }
  .meta-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }
  .meta-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2C2C2C;
  }
  .badge { display: inline-block; padding: 3px 8px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
  .badge-sensitive { background: #FFF0EE; color: #C0392B; }
  .badge-ok { background: #F0FFF4; color: #27AE60; }
  .transcript-container {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 24px;
    margin-top: 4px;
  }
  .transcript-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 20px;
    letter-spacing: -0.01em;
  }
  .transcript-summary {
    font-size: 0.9rem;
    color: #444;
    line-height: 1.6;
    white-space: pre-wrap;
    background: #FAF8F5;
    border-radius: 8px;
    padding: 16px;
  }
  .no-transcript {
    color: #aaa;
    font-size: 0.875rem;
    font-style: italic;
  }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }
  /* Tab bar — groups the per-call panels (Dealer hand, Engagement
     timeline, Call diagnostics, Summary) behind a single switcher so
     the page no longer scrolls through five stacked sections. Visual
     language matches AdminVoices.js so the two pages feel like one
     admin surface. */
  .cd-tabs { display:flex; gap:4px; border-bottom:2px solid #E8E3DC; margin-bottom:20px; flex-wrap:wrap; }
  .cd-tab { background:transparent; border:none; padding:10px 18px; font-size:0.95rem; font-weight:600; color:#888; cursor:pointer; font-family:inherit; border-bottom:3px solid transparent; margin-bottom:-2px; transition:color 0.15s; }
  .cd-tab:hover { color:#2C2C2C; }
  .cd-tab.active { color:#E8845A; border-bottom-color:#E8845A; }
  /* AI Context tab — verbatim system-prompt dump. Monospace so
     formatting-sensitive prompt blocks (bullet lists, indents) survive
     the render. max-height + scroll because long memory snapshots can
     run several screens; line-height tuned for code, not prose. */
  .ai-context-container {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 24px;
  }
  .ai-context-pre {
    font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
    font-size: 0.78rem;
    color: #2C2C2C;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
    background: #FAF8F5;
    border: 1px solid #EDE9E4;
    border-radius: 8px;
    padding: 16px;
    max-height: 70vh;
    overflow-y: auto;
    margin: 0;
  }
  .ai-context-meta {
    display: flex;
    gap: 12px;
    align-items: baseline;
    font-size: 0.78rem;
    color: #888;
    margin-bottom: 12px;
  }
  .ai-context-meta strong { color: #2C2C2C; font-weight: 600; }
  /* #839 — flash animation applied to a Turns-grid row when the
     admin clicks an evidence-turn chip in the Dealer-hand panel.
     The chip handler in CardsHandView.js adds .turn-flash for ~1.6s
     so the admin can see WHICH row was scrolled into view. */
  @keyframes turnFlash {
    0%   { background: #FFF59D; }
    100% { background: transparent; }
  }
  .turn-flash {
    animation: turnFlash 1.6s ease-out;
  }
`;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function AdminConversationDetail() {
  const { id } = useParams();
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Default to Dealer Hand — per #765 it's the primary diagnostic when
  // opening a call view (what Berta was asked to consider and how each
  // card landed). Summary is one click away in the tab bar.
  const [activeTab, setActiveTab] = useState('dealer');

  useEffect(() => {
    adminApi.getConversation(id)
      .then(setConv)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <AdminLayout title="Conversation Detail">
      <style>{styles}</style>

      {/* Page VERSION lives as data-version (not a visible badge) so it
          doesn't get read as belonging to the Dealer Hand tab — the
          panels carry their own VERSION constants. */}
      <div data-page-version={VERSION} />

      <Link to="/admin/conversations" className="back-link">← Back to conversations</Link>

      <div className="internal-banner">
        🔒 Internal — not shared with family
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {conv && (
        <>
          <div className="conv-meta">
            <div className="meta-item">
              <div className="meta-label">User</div>
              <div className="meta-value">{conv.user_name}</div>
            </div>
            <div className="meta-item">
              <div className="meta-label">Started</div>
              <div className="meta-value">{fmtDate(conv.started_at)}</div>
            </div>
            <div className="meta-item">
              <div className="meta-label">Duration</div>
              <div className="meta-value">
                {conv.duration_mins > 0 ? `${conv.duration_mins} min` : '—'}
              </div>
            </div>
            <div className="meta-item">
              <div className="meta-label">Turns</div>
              <div className="meta-value">{conv.turn_count}</div>
            </div>
            <div className="meta-item">
              <div className="meta-label">Language</div>
              <div className="meta-value">{conv.language || '—'}</div>
            </div>
            <div className="meta-item">
              <div className="meta-label">Sensitive</div>
              <div className="meta-value">
                {conv.is_sensitive
                  ? <span className="badge badge-sensitive">Yes</span>
                  : <span className="badge badge-ok">No</span>
                }
              </div>
            </div>
          </div>

          {/* Tab bar — groups the per-call panels behind one switcher so
              the page no longer stacks five panels vertically. Each
              panel still hides itself when its data is empty (e.g.
              pre-cards calls, pre-#151 calls, no voice clips), so
              switching to an empty tab renders nothing rather than an
              error. */}
          <div className="cd-tabs" role="tablist">
            {TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={`cd-tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* #765 Story D View 1 — dealer hand for this call. Primary
              diagnostic when opening a call admin view (Bas's call,
              2026-05-17). Self-contained: hides when the call has no
              card_outcomes rows (calls predating the cards system). */}
          {activeTab === 'dealer' && <CardsHandView convId={id} />}

          {/* Engagement timeline (#151) — what Berta proposed and how the
              user received it. Empty for conversations recorded before
              #151 shipped. */}
          {activeTab === 'timeline' && <EngagementTimeline convId={id} />}

          {/* Call Diagnostics (#702) — per-turn STT confidence, latencies,
              errors. Voice clips (#597) — opt-in mic-input recordings —
              ride along in the same tab; the clips panel hides itself
              when the call has no clips. */}
          {activeTab === 'diagnostics' && (
            <>
              <CallDiagnostics convId={id} />
              <VoiceAudioClips convId={id} />
            </>
          )}

          {activeTab === 'summary' && (
            <div className="transcript-container">
              <div className="transcript-title">Full Summary (Internal)</div>
              {conv.summary
                ? <div className="transcript-summary">{conv.summary}</div>
                : <div className="no-transcript">No summary recorded for this conversation.</div>
              }

              {conv.summary_for_family && (
                <>
                  <div className="transcript-title" style={{ marginTop: 24 }}>Summary for Family</div>
                  <div className="transcript-summary">{conv.summary_for_family}</div>
                </>
              )}
            </div>
          )}

          {/* AI Context — the verbatim system prompt the LLM saw on
              turn 1 (migration 0097). Persona + rules + per-call
              memory snapshot + runtime-context blocks. NULL for calls
              that predate migration 0097 OR for paths where
              chat_stream never fired (missed / quota_declined etc.) —
              both render the same empty state. */}
          {activeTab === 'ai-context' && (
            <div className="ai-context-container">
              <div className="transcript-title">AI Context (turn 1)</div>
              {conv.initial_system_prompt ? (
                <>
                  <div className="ai-context-meta">
                    <span><strong>{conv.initial_system_prompt.length.toLocaleString()}</strong> chars</span>
                    <span>· captured at call start, verbatim</span>
                  </div>
                  <pre className="ai-context-pre">{conv.initial_system_prompt}</pre>
                </>
              ) : (
                <div className="no-transcript">
                  No AI context captured for this call (predates the
                  feature, or the LLM never fired — e.g. missed call,
                  quota-declined, silence-timeout).
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
